import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Storefront } from '../types';

interface UseFunnelSelectionProps {
  funnel: Storefront;
}

/**
 * Manages step selection and URL synchronization.
 * Handles browser navigation and prevents infinite update loops.
 */
export function useFunnelSelection({ funnel }: UseFunnelSelectionProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);

  const hasInitialized = useRef(false);
  const lastProcessedUrlStepId = useRef<string | null>(null);

  // Keep funnel in a ref so url-read effect can validate step existence
  // without re-running when funnel mutates. A funnel mutation during an
  // in-flight setSearchParams would otherwise race with React Router's
  // commit and read a stale URL, flipping selection back.
  const funnelRef = useRef(funnel);
  funnelRef.current = funnel;

  const stepExists = useCallback((stepId: string) => {
    const f = funnelRef.current;
    return f.steps?.some((s) => s.id === stepId) || f.nodes?.some((n) => n.id === stepId) || false;
  }, []);

  // Initialize from URL on mount (once funnel is loaded)
  useEffect(() => {
    if (hasInitialized.current) return;
    if (!funnel.steps?.length && !funnel.nodes?.length) return;

    const stepIdFromUrl = searchParams.get('stepId');
    if (stepIdFromUrl && stepExists(stepIdFromUrl)) {
      lastProcessedUrlStepId.current = stepIdFromUrl;
      setSelectedStepId(stepIdFromUrl);
    }

    hasInitialized.current = true;
  }, [funnel.steps, funnel.nodes, searchParams, stepExists]);

  // Listen for external URL changes (browser back/forward).
  // Only re-run when searchParams changes — never on funnel mutations.
  useEffect(() => {
    if (!hasInitialized.current) return;

    const stepIdFromUrl = searchParams.get('stepId');
    if (stepIdFromUrl === lastProcessedUrlStepId.current) return;

    if (stepIdFromUrl && stepExists(stepIdFromUrl)) {
      lastProcessedUrlStepId.current = stepIdFromUrl;
      setSelectedStepId(stepIdFromUrl);
    } else if (!stepIdFromUrl) {
      lastProcessedUrlStepId.current = null;
      setSelectedStepId(null);
    }
  }, [searchParams, stepExists]);

  // Sync state changes back to URL
  useEffect(() => {
    if (!hasInitialized.current) return;
    if (selectedStepId === lastProcessedUrlStepId.current) return;

    lastProcessedUrlStepId.current = selectedStepId;

    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (selectedStepId) {
        newParams.set('stepId', selectedStepId);
        // Strip studio editor params only when selecting a step (returning to flow view).
        // Do NOT strip when clearing selectedStepId (null) — that happens when
        // opening the visual editor, and stripping would race with setEditorParams.
        newParams.delete('editor');
        newParams.delete('editorStep');
        newParams.delete('editorMode');
      } else {
        newParams.delete('stepId');
        newParams.delete('tab');
        newParams.delete('variant');
      }
      return newParams;
    }, { replace: true });
  }, [selectedStepId, setSearchParams]);

  return {
    selectedStepId,
    setSelectedStepId,
  };
}
